import { invariantResponse } from '@epic-web/invariant';
import { Img } from 'openimg/react';
import { Link, NavLink, Outlet } from 'react-router';
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { cn, getUserImgSrc } from '#app/utils/misc.tsx';
import { useOptionalUser } from '#app/utils/user.ts';
import { type Route } from './+types/_layout.ts';

export async function loader({ params }: Route.LoaderArgs) {
  const owner = await prisma.user.findFirst({
    select: {
      id: true,
      name: true,
      username: true,
      image: { select: { objectKey: true } },
      notes: { select: { id: true, title: true } },
    },
    where: { username: params.username },
  });

  invariantResponse(owner, 'Owner not found', { status: 404 });

  return { owner };
}

export default function NotesRoute({ loaderData }: Route.ComponentProps) {
  const user = useOptionalUser();
  const isOwner = user?.id === loaderData.owner.id;
  const ownerDisplayName = loaderData.owner.name ?? loaderData.owner.username;
  const navLinkDefaultClassName =
    'line-clamp-2 block rounded-l-full py-2 pl-8 pr-6 text-base lg:text-xl';
  return (
    <main className="container flex min-h-[400px] flex-1 px-0 pb-12 md:px-8">
      <div className="bg-muted grid w-full grid-cols-4 pl-2 md:container md:rounded-3xl md:pr-0">
        <div className="relative col-span-1">
          <div className="absolute inset-0 flex flex-col">
            <Link
              to={`/users/${loaderData.owner.username}`}
              className="bg-muted flex flex-col items-center justify-center gap-2 pt-12 pr-4 pb-4 pl-8 xl:flex-row xl:justify-start xl:gap-4"
            >
              <Img
                src={getUserImgSrc(loaderData.owner.image?.objectKey)}
                alt={ownerDisplayName}
                className="size-16 rounded-full object-cover xl:size-24"
                width={256}
                height={256}
              />
              <h1 className="text-center text-base font-bold md:text-lg lg:text-left lg:text-2xl">
                {ownerDisplayName}'s Notes
              </h1>
            </Link>
            <ul className="overflow-x-hidden overflow-y-auto pb-12">
              {isOwner ? (
                <li className="p-1 pr-0">
                  <NavLink
                    to="new"
                    className={({ isActive }) =>
                      cn(navLinkDefaultClassName, isActive && 'bg-accent')
                    }
                  >
                    <Icon name="plus">New Note</Icon>
                  </NavLink>
                </li>
              ) : null}
              {loaderData.owner.notes.map((note) => (
                <li key={note.id} className="p-1 pr-0">
                  <NavLink
                    to={note.id}
                    preventScrollReset
                    prefetch="intent"
                    className={({ isActive }) =>
                      cn(navLinkDefaultClassName, isActive && 'bg-accent')
                    }
                  >
                    {note.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="bg-accent relative col-span-3 md:rounded-r-3xl">
          <Outlet />
        </div>
      </div>
    </main>
  );
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: ({ params }) => (
          <p>No user with the username "{params.username}" exists</p>
        ),
      }}
    />
  );
}
